import { cn } from '@/lib/utils';
import { useSidebar } from '@/contexts/SidebarContext';
import { ChevronDownIcon, ChevronRightIcon } from '@/components/ui';
import { SidebarNavItem } from './SidebarNavItem';
import type { ProcessedNavSection } from '@/lib/navigation';

interface SidebarSectionV2Props {
  section: ProcessedNavSection;
}

export function SidebarSectionV2({ section }: Readonly<SidebarSectionV2Props>) {
  const { expandedSections, toggleSection } = useSidebar();

  const isSectionExpanded = expandedSections[section.id] ?? false;

  const isAdminSection = section.id === 'data-quality-admin' || section.id.startsWith('raw-data') || section.id === 'site-admin';

  return (
    <div className={cn('space-y-1', isAdminSection && 'rounded-md mx-1 px-1 bg-muted/30')}>
      {/* Section divider */}
      {section.divider && <div className="mx-2 my-2 border-t border-border" />}

      {/* Collapsible section header */}
      {section.label && (
        <button
          type="button"
          onClick={() => toggleSection(section.id)}
          className={cn(
            'flex w-full items-center justify-between rounded-md px-3 py-2',
            'text-xs font-semibold uppercase tracking-wider',
            'text-muted-foreground',
            'hover:bg-accent/50 transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
          aria-expanded={isSectionExpanded}
          aria-controls={`nav-section-${section.id}`}
        >
          <span>{section.label}</span>
          {isSectionExpanded ? (
            <ChevronDownIcon className="h-4 w-4 text-muted-foreground transition-transform duration-300" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-muted-foreground transition-transform duration-300" />
          )}
        </button>
      )}

      {/* Collapsible items container */}
      <div
        id={`nav-section-${section.id}`}
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          !isSectionExpanded && section.label
            ? 'max-h-0 opacity-0'
            : 'max-h-[2000px] opacity-100'
        )}
      >
        <div className="space-y-1">
          {section.items.map((item) => (
            <SidebarNavItem
              key={item.id}
              href={item.href}
              icon={item.icon}
              label={item.label}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
